import type { PaymentMethod, PaymentStatus } from '@stayafrica/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Banknote,
  Coins,
  List,
  Plus,
  RotateCcw,
  Search,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { paymentsSummaryQueryOptions } from '@/features/analytics/queries';
import { useCan } from '@/features/auth/session';
import { PaymentDrawer } from '@/features/payments/PaymentDrawer';
import { cashOverviewQueryOptions, paymentsQueryOptions } from '@/features/payments/queries';
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  methodTone,
  paymentBadge,
} from '@/features/payments/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatTime } from '@/lib/format';

interface PaymentsSearch {
  q?: string;
  method?: string;
  unallocated?: boolean;
  /** Ouvre le tiroir « Enregistrer un paiement » (⌘K, liens externes). */
  new?: string;
}

export const Route = createFileRoute('/_app/billing/payments/')({
  validateSearch: (search: Record<string, unknown>): PaymentsSearch => ({
    ...(typeof search['q'] === 'string' ? { q: search['q'] } : {}),
    ...(typeof search['method'] === 'string' ? { method: search['method'] } : {}),
    ...(search['unallocated'] === true || search['unallocated'] === 'true'
      ? { unallocated: true }
      : {}),
    ...(search['new'] === 'payment' ? { new: 'payment' } : {}),
  }),
  component: PaymentsPage,
});

function PaymentsPage() {
  const { t, i18n } = useTranslation(['payments', 'cash', 'billing', 'common', 'errors']);
  const search = Route.useSearch();
  const canRead = useCan('payment.read');
  const canRecord = useCan('payment.record');
  const canReadCash = useCan('cash.read');

  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  const [methods, setMethods] = useState<PaymentMethod[]>(
    search.method ? [search.method as PaymentMethod] : [],
  );
  const [statuses, setStatuses] = useState<PaymentStatus[]>([]);
  const [unallocatedOnly, setUnallocatedOnly] = useState(search.unallocated === true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(search.new === 'payment');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const filters = {
    ...(debounced ? { q: debounced } : {}),
    ...(methods.length === 1 ? { method: methods[0] } : {}),
    ...(statuses.length === 1 ? { status: statuses[0] } : {}),
    ...(unallocatedOnly ? { unallocatedOnly: true } : {}),
    ...(from ? { from: `${from}T00:00:00.000Z` } : {}),
    ...(to ? { to: `${to}T23:59:59.999Z` } : {}),
  };

  const query = useInfiniteQuery(paymentsQueryOptions(filters, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  // KPI : servis par `GET /payments/summary` (sprint 2.7). L'agrégation
  // cliente sur les 100 derniers paiements du mois a disparu : elle était
  // fausse dès que le mois dépassait une page.
  const summaryQuery = useQuery(paymentsSummaryQueryOptions(canRead));
  const cash = useQuery(cashOverviewQueryOptions(canReadCash));

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const summary = summaryQuery.data;
  const currency = summary?.currencyCode ?? 'XOF';
  const methodMax = Math.max(1, ...(summary?.byMethod ?? []).map((entry) => entry.amount));

  const toggleMethod = (method: PaymentMethod) =>
    setMethods((current) => (current.includes(method) ? [] : [method]));
  const toggleStatus = (status: PaymentStatus) =>
    setStatuses((current) => (current.includes(status) ? [] : [status]));

  const hasFilters =
    debounced !== '' ||
    methods.length > 0 ||
    statuses.length > 0 ||
    unallocatedOnly ||
    from !== '' ||
    to !== '';

  const periodTotal = rows
    .filter((row) => row.status !== 'REVERSED' && row.kind !== 'REFUND')
    .reduce((sum, row) => sum + row.amount, 0);

  return (
    <Container>
      <PageHeader
        title={t('payments:list.title')}
        subtitle={t('payments:list.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('payments:list.title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            {canReadCash ? (
              <Button variant="secondary" asChild>
                <Link to="/billing/cash">
                  <Wallet className="ico" aria-hidden="true" />
                  {t('cash:title')}
                </Link>
              </Button>
            ) : null}
            {canRecord ? (
              <Button variant="primary" onClick={() => setDrawerOpen(true)}>
                <Plus className="ico" aria-hidden="true" />
                {t('payments:actions.record')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="green"
            icon={<Banknote className="ico ico-16" aria-hidden="true" />}
            label={t('payments:kpi.today')}
            value={formatAmount(summary?.today.collected ?? 0, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={t('payments:kpi.todayFoot', { count: summary?.today.count ?? 0 })}
          />
          <KpiCard
            tone="violet"
            icon={<Coins className="ico ico-16" aria-hidden="true" />}
            label={t('payments:kpi.month')}
            value={formatAmount(summary?.month.collected ?? 0, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={t('payments:kpi.monthCount', { count: summary?.month.count ?? 0 })}
          />
          <KpiCard
            tone="amber"
            icon={<TriangleAlert className="ico ico-16" aria-hidden="true" />}
            label={t('payments:kpi.unallocated')}
            value={formatAmount(summary?.unallocated.amount ?? 0, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={t('payments:kpi.unallocatedCount', { count: summary?.unallocated.count ?? 0 })}
          />
          <KpiCard
            tone="red"
            icon={<RotateCcw className="ico ico-16" aria-hidden="true" />}
            label={t('payments:kpi.refunds')}
            value={formatAmount(summary?.month.refunds ?? 0, i18n.language, currency)}
            loading={summaryQuery.isPending}
            footer={
              cash.data?.currentSession
                ? t('payments:kpi.cashOpen', {
                    amount: formatAmount(cash.data.currentSession.expectedBalance, i18n.language),
                  })
                : t('payments:kpi.refundsFoot')
            }
          />
        </div>

        {summary && summary.byMethod.length > 0 ? (
          <Card>
            <CardHeader
              title={t('payments:kpi.byMethodTitle')}
              icon={<Coins className="ico ico-16" aria-hidden="true" />}
              iconTone="green"
              actions={<span className="t-12 muted">{t('payments:kpi.byMethodFoot')}</span>}
            />
            <CardBody>
              <div className="hbars">
                {[...summary.byMethod]
                  .sort((a, b) => b.amount - a.amount)
                  .map((entry) => (
                    <div className="hbar" key={`${entry.method}-${entry.provider ?? ''}`}>
                      <span className="hbar__label">
                        {entry.provider
                          ? t(`payments:provider.${entry.provider}`)
                          : t(`payments:method.${entry.method}`)}
                      </span>
                      <span className="hbar__track">
                        <span
                          className="hbar__fill"
                          style={{
                            width: `${Math.max((entry.amount / methodMax) * 100, entry.amount > 0 ? 2 : 0)}%`,
                          }}
                        />
                      </span>
                      <span className="hbar__value">
                        {formatAmount(entry.amount, i18n.language, currency)}
                      </span>
                      <span className="hbar__sub">
                        {t('payments:kpi.byMethodCount', { count: entry.count })}
                      </span>
                    </div>
                  ))}
              </div>
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader
            title={t('payments:list.tableTitle')}
            icon={<List className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              <Badge tone="neutral">{t('payments:list.countLabel', { count: rows.length })}</Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('payments:list.searchPlaceholder')}
              aria-label={t('payments:list.searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 300, height: 32, fontSize: 'var(--fs-13)' }}
            />
            {PAYMENT_METHODS.map((method) => (
              <ChipFilter
                key={method}
                active={methods.includes(method)}
                onClick={() => toggleMethod(method)}
              >
                {t(`payments:method.${method}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          <Toolbar>
            {PAYMENT_STATUSES.map((status) => (
              <ChipFilter
                key={status}
                active={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
              >
                {t(`payments:status.${status}`)}
              </ChipFilter>
            ))}
            <ChipFilter
              active={unallocatedOnly}
              onClick={() => setUnallocatedOnly((value) => !value)}
            >
              {t('payments:list.filters.unallocated')}
            </ChipFilter>
            <Input
              type="date"
              aria-label={t('payments:list.filters.from')}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <Input
              type="date"
              aria-label={t('payments:list.filters.to')}
              value={to}
              onChange={(event) => setTo(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <span className="spacer" />
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerm('');
                  setDebounced('');
                  setMethods([]);
                  setStatuses([]);
                  setUnallocatedOnly(false);
                  setFrom('');
                  setTo('');
                }}
              >
                {t('common:actions.clearFilters')}
              </Button>
            ) : null}
          </Toolbar>

          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={7} columns={7} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<Banknote className="ico ico-24" aria-hidden="true" />}
                title={
                  hasFilters ? t('payments:list.emptySearchTitle') : t('payments:list.emptyTitle')
                }
                description={
                  hasFilters ? t('payments:list.emptySearchBody') : t('payments:list.emptyBody')
                }
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('payments:columns.reference')}</Th>
                      <Th>{t('payments:columns.date')}</Th>
                      <Th>{t('payments:columns.customer')}</Th>
                      <Th>{t('payments:columns.method')}</Th>
                      <Th>{t('payments:columns.allocation')}</Th>
                      <Th align="right">{t('payments:columns.amount')}</Th>
                      <Th>{t('payments:columns.status')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const badge = paymentBadge(row);
                      return (
                        <Tr key={row.id}>
                          <Td>
                            <Link to="/billing/payments/$paymentId" params={{ paymentId: row.id }}>
                              <span className="cell-id">{row.reference}</span>
                            </Link>
                            {row.transactionReference ? (
                              <div className="t-12 muted mono">{row.transactionReference}</div>
                            ) : null}
                          </Td>
                          <Td className="t-13" numeric>
                            {formatDateShort(row.paidAt, i18n.language)}
                            <div className="t-12 muted">
                              {formatTime(row.paidAt, i18n.language)}
                            </div>
                          </Td>
                          <Td>
                            <UserCell
                              avatar={<Avatar name={row.customerName ?? '—'} size="sm" />}
                              name={row.customerName ?? t('payments:list.noCustomer')}
                              subtitle={row.recordedByName ?? undefined}
                            />
                          </Td>
                          <Td>
                            <Badge tone={methodTone(row.method, row.provider)}>
                              {row.provider
                                ? t(`payments:provider.${row.provider}`)
                                : t(`payments:method.${row.method}`)}
                            </Badge>
                          </Td>
                          <Td className="t-13">
                            {row.allocations.length === 0 ? (
                              <span className="muted">{t('payments:list.notAllocated')}</span>
                            ) : (
                              row.allocations
                                .map(
                                  (allocation) =>
                                    allocation.invoiceNumber ??
                                    allocation.reservationReference ??
                                    '—',
                                )
                                .join(', ')
                            )}
                            {row.unallocatedAmount > 0 ? (
                              <Badge tone="amber" className="ml-2">
                                {t('payments:list.toAllocate', {
                                  amount: formatAmount(row.unallocatedAmount, i18n.language),
                                })}
                              </Badge>
                            ) : null}
                          </Td>
                          <Td align="right" numeric>
                            <span
                              className="strong"
                              style={{
                                color: row.kind === 'REFUND' ? 'var(--danger)' : undefined,
                                textDecoration:
                                  row.status === 'REVERSED' ? 'line-through' : undefined,
                              }}
                            >
                              {row.kind === 'REFUND' ? '−' : ''}
                              {formatAmount(row.amount, i18n.language)}
                            </span>
                          </Td>
                          <Td>
                            <Badge tone={badge.tone} dot>
                              {t(`payments:${badge.key}`)}
                            </Badge>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <Td colSpan={5} align="right" className="t-13">
                        {t('payments:list.periodTotal')}
                      </Td>
                      <Td align="right" numeric strong>
                        {formatAmount(periodTotal, i18n.language)}
                      </Td>
                      <Td />
                    </tr>
                  </tfoot>
                </Table>
              </TableWrap>
            )}
          </CardBody>

          {query.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <PaymentDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </Container>
  );
}
